import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly, TeamHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { SubtitlesGenerationPrompt } from '@/types/resources';
import { api } from '@/utils/api';

import Form from './components/Form';
import TableRow from './components/TableRow';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const StyledButton = styled(Button)`
  background: transparent;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const SubtitlesGenerationPrompts = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const navigate = useNavigate();

  const { data, isError, isLoading } = useQuery(
    [
      'subtitles_generation_prompts',
      {
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<never, SubtitlesGenerationPrompt[]>(
        '/v1/distribution/subtitles-generation-prompts',
        {
          params: {
            ...(!!debouncedSearch && { 'filter[search]': debouncedSearch }),
          },
        },
      ),
  );

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Subtitles Generator Prompts</PageHeading>
        <HeaderButtons>
          <StyledButton
            color="secondary"
            onClick={() => navigate('../pending', { relative: 'path' })}
          >
            Projects
          </StyledButton>
          <Button onClick={() => setShowCreationForm(true)}>
            Create prompt
          </Button>
        </HeaderButtons>
      </HeadingRow>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by title"
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Created At</Th>
              <Th>Created By</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.length}
            >
              {data?.map((prompt) => (
                <TableRow key={prompt.id} prompt={prompt} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showCreationForm && (
          <Form onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default SubtitlesGenerationPrompts;
